import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ChartConfiguration } from 'chart.js/auto';
import { buildBarChartData, BarChartData } from '../../utils/demographic-chart';
import { ChartConfig } from '../../utils/chart-config';

/**
 * Card demográfica con gráfico de barras verticales (eje Y = cantidad, X = categoría).
 *
 * Reemplaza el patrón anterior de lista scrollable + barras horizontales custom
 * de las 6 cards demográficas del tab analytics. Aplica Top N + "Otros" para
 * mantener legible el eje X cuando hay muchas categorías (e.g. profesión).
 */
@Component({
  selector: 'app-demographic-bar-chart',
  templateUrl: './demographic-bar-chart.component.html',
  styleUrls: ['./demographic-bar-chart.component.css']
})
export class DemographicBarChartComponent implements OnChanges {

  @Input() title = '';
  @Input() items: any[] | null = null;
  @Input() labelKey = '';
  @Input() valueKey = 'count';
  @Input() color: string = ChartConfig.COLORS.primary;
  @Input() topN = 10;
  @Input() totalLabel = '';
  @Input() emptyIcon = 'fa-chart-bar';
  @Input() emptyText = 'No hay datos';
  @Input() formatLabel?: (raw: any) => string;

  chartConfig: ChartConfiguration | null = null;
  chartData: BarChartData = { labels: [], values: [], total: 0, truncated: 0 };

  ngOnChanges(changes: SimpleChanges): void {
    // Cualquier cambio en inputs que afectan la serie rebuilda el config.
    // Inputs estáticos del template (title, emptyIcon...) no requieren rebuild.
    const dataKeys = ['items', 'labelKey', 'valueKey', 'color', 'topN', 'formatLabel'];
    const shouldRebuild = dataKeys.some(k => changes[k]);
    if (shouldRebuild) {
      this.rebuildChart();
    }
  }

  get hasData(): boolean {
    return this.chartData.labels.length > 0;
  }

  get originalCount(): number {
    return this.items?.length ?? 0;
  }

  private rebuildChart(): void {
    this.chartData = buildBarChartData(
      this.items,
      this.labelKey,
      this.valueKey,
      this.topN,
      this.formatLabel
    );

    if (!this.hasData) {
      this.chartConfig = null;
      return;
    }

    const total = this.chartData.total;
    const baseOpts = ChartConfig.getVerticalBarConfig();

    this.chartConfig = {
      type: 'bar',
      data: {
        labels: this.chartData.labels,
        datasets: [{
          label: 'Cantidad',
          data: this.chartData.values,
          backgroundColor: this.color,
          borderColor: this.color,
          borderWidth: 1,
          borderRadius: 4
        }]
      },
      options: {
        ...baseOpts,
        plugins: {
          ...baseOpts.plugins,
          legend: { display: false },
          tooltip: {
            ...baseOpts.plugins.tooltip,
            callbacks: {
              label: (ctx: any) => {
                const value = Number(ctx.parsed.y ?? 0);
                const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return `${value.toLocaleString('es-ES')} (${pct}%)`;
              }
            }
          }
        },
        scales: {
          ...baseOpts.scales,
          x: {
            ...baseOpts.scales.x,
            ticks: {
              ...baseOpts.scales.x.ticks,
              maxRotation: 45,
              minRotation: 0,
              autoSkip: false
            }
          }
        }
      }
    } as ChartConfiguration;
  }
}
